import React, { useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { Col, Form } from 'react-bootstrap'
import { Modal } from '../../components/Modal/Modal'
import { Button } from '../../components/Button/Button'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId } from '../../store/auth/authSlice'

import { ReactComponent as ZocksLogo } from '../../assets/ZocksLogo.svg'
import { ReactComponent as Sparkling } from '../../assets/images/sparkling.svg'
import { useExecuteAiAgentMutation, useInteractWithAiAgentMutation } from '../../store/ai/aiAgent'
import { AIAgentType } from '../../models/AIAgent'
import { AskAnythingResultDisplay } from './AskAnythingResultDisplay'
import { MeetingContainer } from '../../components/AskAnythingContainer/MeetingContainer'
import { AskAnythingExecutionResult, AskAnythingInteractionResult } from '../../models/AskAnything'
import { AgentExamples } from '../AIAgent/Chat/AgentExamples'

export interface AskAnythingContactModalProps {
  contactId: string
  show: boolean
  onCancel: () => void
  displayName: string
}

interface AskAnythingQuestion {
  type: 'question'
  question: string
}

interface AskAnythingResult {
  type: 'result'
  result: { [key: string]: any }
  metadata: { [key: string]: any }
}

type AskAnythingMessage = AskAnythingQuestion | AskAnythingResult

const _METADATA_KEY = 'Metadata'

export const AskAnythingContactModal: React.FC<AskAnythingContactModalProps> = ({ contactId, show, onCancel, displayName }) => {
  const accountId = useAppSelector(selectAccountId)
  const { tracked } = useTracking()

  const [executeAiAgent, { isLoading: executeIsLoading }] = useExecuteAiAgentMutation()
  const [interactWithAiAgent, { isLoading: interactIsLoading }] = useInteractWithAiAgentMutation()

  const [messages, setMessages] = useState<AskAnythingMessage[]>([])
  const [aiAgentId, setAiAgentId] = useState<string | null>(null)

  const _executeAskMeAnything = async (question: string) => {
    try {
      setMessages((messages) => [...messages, { type: 'question', question }])
      if (!aiAgentId) {
        const askAnythingResult = (await executeAiAgent({
          accountId,
          payload: {
            type: AIAgentType.ASK_ANYTHING,
            context: {
              contactId,
              question,
            },
          },
        }).unwrap()) as AskAnythingExecutionResult
        const { aiAgentId } = askAnythingResult
        setAiAgentId(aiAgentId)
        parseAskAnythingResult(question, askAnythingResult)
      } else {
        const askAnythingResult = (await interactWithAiAgent({
          accountId,
          aiAgentId,
          payload: { question },
        }).unwrap()) as AskAnythingInteractionResult
        parseAskAnythingResult(question, askAnythingResult)
      }
    } catch (e) {
      console.error(e)
    }
  }

  const parseAskAnythingResult = (question: string, askAnythingResult: AskAnythingExecutionResult | AskAnythingInteractionResult) => {
    const { result } = askAnythingResult
    const { [_METADATA_KEY]: metadata } = result
    const resultWithoutMetadata = Object.keys(result)
      .filter((key) => key !== _METADATA_KEY)
      .reduce((obj: { [key: string]: any }, key) => {
        obj[key] = result[key]
        return obj
      }, {})
    setMessages((messages) => [
      ...messages,
      {
        type: 'result',
        result: resultWithoutMetadata,
        ...(metadata && { metadata }),
      },
    ])
  }

  const onAsk = tracked(
    _executeAskMeAnything,
    {
      eventName: TrackEvent.AI_ASK_ME_ANYTHING_EXECUTED,
      eventDetails: { accountId, contactId },
    },
    { eventDetailsExtractor: (question: string) => ({ question }) },
  )

  const body = <AskAnythingContactModalBody onAsk={onAsk} isLoading={executeIsLoading || interactIsLoading} messages={messages} displayName={displayName} />

  return (
    <Modal
      show={show}
      onHide={onCancel}
      body={body}
      header={
        <Col className='d-flex gap-2 align-items-center justify-content-center'>
          <h4>Ask Anything</h4>
        </Col>
      }
    />
  )
}

interface AskAnythingContactModalBodyProps {
  onAsk: (question: string) => Promise<void>
  messages: AskAnythingMessage[]
  isLoading: boolean
  displayName: string
}

const AskAnythingContactModalBody: React.FC<AskAnythingContactModalBodyProps> = ({ onAsk, isLoading, messages, displayName }) => {
  const examples = [`What is ${displayName}'s financial goal?`, `How many children does ${displayName} have?`, `What is ${displayName}'s annual income?`]

  return (
    <div className='mb-4 d-flex flex-column gap-3'>
      <div className='d-flex py-3 px-2 align-items-center gap-3 ai-agent-chat-header'>
        <div className='d-flex align-items-center justify-content-center bg-black ai-agent-chat-header-logo zocks-border-0.25'>
          <ZocksLogo width={18} height={18} />
        </div>
        <div className='d-flex flex-column'>
          <span className='paragraph-small'>ZOCKS</span>
          <span className='paragraph-small'>AI Assistant</span>
        </div>
      </div>
      <div className='d-flex flex-column justify-content-between gap-3 flex-grow-1 overflow-y-auto ai-agent-messages-container'>
        <AskAnythingContactMessages messages={messages} isLoading={isLoading} displayName={displayName} />
        <AgentExamples className='mt-auto' handleSubmit={onAsk} examples={examples} toggleLabel='Example questions' />
      </div>
      <AskAnythingContactInput onAsk={onAsk} isLoading={isLoading} />
    </div>
  )
}

const MAX_CHAT_MESSAGE_LENGTH = 100

interface AskAnythingContactInputProps {
  onAsk: (question: string) => Promise<void>
  isLoading: boolean
}

export const AskAnythingContactInput: React.FC<AskAnythingContactInputProps> = ({ onAsk, isLoading }) => {
  const textRef = useRef<HTMLTextAreaElement>(null)

  const [chatMessage, setChatMessage] = useState<string>('')
  const [wasTruncated, setWasTruncated] = useState<boolean>(false)

  useEffect(() => {
    if (!textRef.current) {
      return
    }
    textRef.current.style.height = '0px'
    const newHeight = Math.min(textRef.current.scrollHeight, 128)
    textRef.current.style.height = newHeight + 'px'
  }, [textRef, chatMessage])

  const remainingCharacters = MAX_CHAT_MESSAGE_LENGTH - chatMessage.trim().length
  const isAtLimit = remainingCharacters === 0
  const isNearLimit = remainingCharacters < 20 && remainingCharacters > 0

  return (
    <div>
      <div className='d-flex justify-content-between pb-2'>
        <div className='d-flex align-items-end paragraph-extra-small zocks-text-warning-text min-height-2'>
          {wasTruncated ? <i className='bi bi-exclamation-triangle'></i> : null}
          {wasTruncated ? <span className='ms-2'>Input truncated to limit</span> : null}
        </div>
        <div
          className={classNames('d-flex align-items-end paragraph-extra-small px-2 min-height-2', {
            'zocks-text-gray-600': !isNearLimit && !isAtLimit,
            'zocks-text-warning-text': isNearLimit,
            'zocks-text-danger-200': isAtLimit,
          })}
        >
          {chatMessage.trim().length > 0 ? (isAtLimit ? 'Limit reached' : `${remainingCharacters} characters left`) : null}
        </div>
      </div>

      <div className='d-flex p-2 align-items-center ai-agent-chat-textarea-container'>
        <div className='d-flex flex-grow-1 gap-3 zocks-px-0.75'>
          <div className='d-flex gap-2 align-items-center flex-grow-1'>
            <Sparkling
              className={classNames({
                'zocks-text-gray-900': chatMessage.trim() && !isLoading,
                'zocks-text-gray-500': !chatMessage.trim() || isLoading,
              })}
            />
            <Form.Control
              ref={textRef}
              className='px-0 border-0'
              as='textarea'
              value={chatMessage}
              maxLength={MAX_CHAT_MESSAGE_LENGTH}
              onPaste={(e: React.ClipboardEvent<HTMLTextAreaElement>) => {
                const { selectionStart = 0, selectionEnd = 0 } = e.currentTarget
                const pastedLength = e.clipboardData.getData('text').length
                const resultingLength = chatMessage.length - (selectionEnd - selectionStart) + pastedLength

                if (resultingLength > MAX_CHAT_MESSAGE_LENGTH) {
                  setWasTruncated(true)
                }
              }}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                const newValue = e.currentTarget.value
                setWasTruncated(wasTruncated && newValue.length >= MAX_CHAT_MESSAGE_LENGTH)
                setChatMessage(newValue)
              }}
              onKeyDown={async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                if (isLoading) {
                  return
                }
                if (e.key === 'Enter') {
                  e.preventDefault()
                  if (e.ctrlKey || e.metaKey) {
                    setChatMessage((chatMessage) => `${chatMessage}\n`)
                  } else if (chatMessage.trim().length >= 3 && chatMessage.trim().length <= MAX_CHAT_MESSAGE_LENGTH) {
                    setChatMessage('')
                    await onAsk(chatMessage)
                  }
                }
              }}
              placeholder='Ask anything…'
            />
          </div>
        </div>
        <div className='d-flex gap-1 justify-content-end align-items-center'>
          <Button
            variant='secondary'
            className='button-medium px-1 py-2 border-0 bg-white'
            onClick={async () => {
              setChatMessage('')
              await onAsk(chatMessage)
              textRef.current?.focus()
            }}
            loading={isLoading}
            disabled={chatMessage.trim().length < 3 || chatMessage.trim().length > MAX_CHAT_MESSAGE_LENGTH}
          >
            <i className='bi bi-arrow-right-circle-fill fs-4' />
          </Button>
        </div>
      </div>
    </div>
  )
}

interface AskAnythingContactMessagesProps {
  isLoading: boolean
  messages: AskAnythingMessage[]
  displayName: string
}

const AskAnythingContactMessages: React.FC<AskAnythingContactMessagesProps> = ({ isLoading, messages, displayName }) => {
  return (
    <div className='d-flex flex-column zocks-gap-1.25'>
      <div className='d-flex zocks-gap-0.75 px-2'>
        <div className='d-flex align-items-center justify-content-center bg-black ai-agent-chat-message-logo zocks-border-0.25'>
          <ZocksLogo width={12} height={12} />
        </div>
        <div className='ai-agent-chat-message paragraph-medium'>Welcome! Ask anything about {displayName}!</div>
      </div>
      {messages.map((message, idx) => {
        const { type } = message
        return type === 'question' ? (
          <AskAnythingContactQuestionMessage key={idx} askAnythingQuestion={message} />
        ) : (
          <AskAnythingContactResultMessage key={idx} askAnythingResult={message} />
        )
      })}
      {isLoading ? (
        <div className='d-flex zocks-gap-0.75 px-2'>
          <div className='d-flex align-items-center justify-content-center bg-black ai-agent-chat-message-logo zocks-border-0.25'>
            <ZocksLogo width={12} height={12} />
          </div>
          <span className='paragraph-medium zocks-text-gray-500'>Thinking</span>
          <div className='ai-agent-chat-loader' />
        </div>
      ) : null}
    </div>
  )
}

interface AskAnythingContactQuestionMessageProps {
  askAnythingQuestion: AskAnythingQuestion
}

const AskAnythingContactQuestionMessage: React.FC<AskAnythingContactQuestionMessageProps> = ({ askAnythingQuestion }) => {
  const { question } = askAnythingQuestion

  return (
    <>
      {question ? (
        <div className='d-flex px-2 justify-content-end'>
          <div className='zocks-p-0.75 ai-agent-chat-message zocks-border-0.5 zocks-bg-color-primary-light'>{question}</div>
        </div>
      ) : null}
    </>
  )
}

interface AskAnythingContactResultMessageProps {
  askAnythingResult: AskAnythingResult
}

const AskAnythingContactResultMessage: React.FC<AskAnythingContactResultMessageProps> = ({ askAnythingResult }) => {
  const { result, metadata } = askAnythingResult

  const [roomId, sessionId] = useMemo(() => {
    return [metadata?.room_id ?? null, metadata?.session_id ?? null]
  }, [metadata])

  return (
    <>
      {result ? (
        <div className='d-flex zocks-gap-0.75 px-2'>
          <div className='d-flex align-items-center justify-content-center bg-black ai-agent-chat-message-logo zocks-border-0.25 mt-1'>
            <ZocksLogo width={12} height={12} />
          </div>
          <div className='d-flex flex-column zocks-gap-1.25 w-100'>
            <div className='paragraph-medium'>
              <AskAnythingResultDisplay result={result} />
              {roomId && sessionId ? <MeetingContainer roomId={roomId} sessionId={sessionId} /> : null}
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
